import React, { createContext, useContext, useState } from 'react'
import { styled } from '@mui/material'
import { Typography, IconButton } from '@evocolabs/orkaui'
import theme from '@evocolabs/orkaui/lib/theme.cjs'
import ArrowLeft24 from '@evocolabs/icons/lib/ArrowLeft24'
import Introduction from '@/components/Schedule/Introduction'
import AppointmentScheduled from '@/components/Schedule/AppointmentScheduled'
import AppointmentCancelled from '@/components/Schedule/AppointmentCancelled'
import TimeSelection from '@/components/Schedule/TimeSelection'
import {
  floorToHour,
  useCalendar
} from '@/components/Schedule/Calendar/CalendarProvider'
import { useBackend } from '@/src/providers/BackendProvider'
import { ErrorDialog } from '@/components/Schedule/AlertDialog'

const ScheduleHeader = () => {
  const HeaderDiv = styled('div')`
    display: flex;
    width: 100%;
    background-color: ${theme.palette.white.main};
    height: 64px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    align-items: center;
    margin-bottom: 72px;
    position: absolute;
    top: 0;
    left: 0;
    ${(props) => props.theme.breakpoints.down('md')} {
      height: 48px;
      margin-bottom: 20px;
    }
  `
  const { setViewId, history } = useSchedule()

  function handleBack() {
    if (history.length === 0) {
      window.history.back()
    } else {
      setViewId(history[history.length - 1])
      history.pop()
    }
  }

  return (
    <HeaderDiv>
      <div
        id="schedule_arrowBack"
        style={{ marginLeft: '32px', cursor: 'pointer', zIndex: '999' }}
      >
        <IconButton onClick={handleBack}>
          <ArrowLeft24
            forceStroke={theme.palette.grey[600]}
            forceFill={theme.palette.grey[600]}
          />
        </IconButton>
      </div>
      <div style={{ width: '100%', position: 'absolute' }}>
        <Typography variant="subh1" style={{ textAlign: 'center' }}>
          Contact Audiologist
        </Typography>
      </div>
    </HeaderDiv>
  )
}

const viewMap = {
  0: <Introduction />,
  1: <TimeSelection />,
  2: <AppointmentScheduled />,
  3: <AppointmentCancelled />
}

export default function ScheduleRoot({ header, params }) {
  const [viewId, setViewId] = useState(params && params.status ? 2 : 0)
  const [history, setHistory] = useState([])
  const { createAppointment, rescheduleAppointment, cancelAppointment } =
    useBackend()
  const { availableTimeSlots, availableSlots, TZ } = useCalendar()
  const [date, setDate] = useState({
    date: params.id ? new Date(params.start_time).getDate() : null,
    month: params.id ? new Date(params.start_time).getMonth() : null,
    year: params.id ? new Date(params.start_time).getFullYear() : null
  })
  const tempTime = params.id
    ? floorToHour(new Date(params.start_time)).getHours() +
      new Date(params.start_time).getMinutes() / 60
    : -1
  const [time, setTime] = useState({
    idx: -1,
    start: params.id ? tempTime : -1,
    end: params.id ? tempTime + 0.5 : -1
  })
  const [info, setInfo] = useState({
    name: '',
    email: '',
    phone: ''
  })
  const [errorOpen, setErrorOpen] = useState(false)

  async function schedule() {
    const vacancy = new Date(
      availableTimeSlots[availableSlots[time.idx] - 1].start_time
    )
    const selection = new Date(
      date.year,
      date.month,
      date.date,
      time.start,
      (time.start % 1) * 60
    )
    const start = vacancy.getTime() > selection.getTime() ? vacancy : selection
    const agent = availableTimeSlots[availableSlots[time.idx] - 1].agent_id
    const data = {
      user_name: info.name,
      email: info.email,
      phone: info.phone,
      start_time: start.toISOString(),
      end_time: new Date(start.getTime() + 30 * 60 * 1000).toISOString(),
      agent: agent,
      user_timezone: TZ
    }
    return await createAppointment(data)
  }

  async function reschedule(token, id) {
    const vacancy = new Date(
      availableTimeSlots[availableSlots[time.idx] - 1].start_time
    )
    const selection = new Date(
      date.year,
      date.month,
      date.date,
      time.start,
      (time.start % 1) * 60
    )
    const start = vacancy.getTime() > selection.getTime() ? vacancy : selection
    const agent = availableTimeSlots[availableSlots[time.idx] - 1].agent_id
    const data = {
      start_time: start.toISOString(),
      end_time: new Date(start.getTime() + 30 * 60 * 1000).toISOString(),
      agent: agent,
      token: token
    }
    return await rescheduleAppointment(data, id)
  }

  async function cancel(token, id) {
    const data = {
      status: 'canceled',
      token: token
    }
    return await cancelAppointment(data, id)
  }

  return (
    <>
      <ScheduleContext.Provider
        value={{
          viewId,
          setViewId,
          history,
          setHistory,
          date,
          setDate,
          time,
          setTime,
          info,
          setInfo,
          params,
          errorOpen,
          setErrorOpen,
          schedule,
          reschedule,
          cancel
        }}
      >
        {header && <ScheduleHeader />}
        {viewMap[viewId]}
        <ErrorDialog open={errorOpen} setOpen={setErrorOpen} />
      </ScheduleContext.Provider>
    </>
  )
}

const ScheduleContext = createContext()
const useSchedule = () => useContext(ScheduleContext)
export { ScheduleContext, useSchedule }
